import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { TRANSIT_MESSAGE_KEYS, EXT_MANAGER_KEY, MESSAGE_TYPES } from '../../cfg/endpoints';
import { CHILD_MESSAGE_TYPES } from './useGetWindowMessages';

const MESSAGE_KEYS = {
  TYPE: 'type',
  SPREADSHEET_ID: 'spreadsheet_id',
  SHEET_GID: 'sheet_gid',
  VALUE: 'value',
};

interface UseSendWindowMessagesReturn {
  sendGrantedAccess: (accessToken: string, spreadsheetId: string) => void;
  sendAuthStatus: (spreadsheetId: string, isAuthorized: boolean) => void;
  sendColumnOptions: (spreadsheetId: string, columns: any[]) => void;
  sendTaskData: (spreadsheetId: string, data: any) => void;
  sendPaginatedTaskData: (spreadsheetId: string, data: any) => void;
  sendMessageToParent: (type: string, payload?: any) => void;
}

export function useSendWindowMessages(): UseSendWindowMessagesReturn {
  const sendMessageToParent = (type: string, payload?: any) => {
    if (window.parent && window.parent !== window && document.location.ancestorOrigins && document.location.ancestorOrigins.length > 0) {
      const targetOrigin = document.location.ancestorOrigins[0];
      try {
        const message_info = { 
          [MESSAGE_KEYS.TYPE]: type, 
          ...payload,
        };
        const message = {
          [TRANSIT_MESSAGE_KEYS.TYPE]: MESSAGE_TYPES.TRELLUS_ROUTER_PASSING,
          [TRANSIT_MESSAGE_KEYS.SOURCE_INFO]: { [EXT_MANAGER_KEY.MANAGER_TYPE]: 'EXTERNAL'},
          [TRANSIT_MESSAGE_KEYS.TARGET_INFO]: { 
            [EXT_MANAGER_KEY.MANAGER_TYPE]: 'INTEGRATOR',
            [EXT_MANAGER_KEY.MANAGER_COLLECTION_GROUP]: 'PLATFORM_API'
          },
          [TRANSIT_MESSAGE_KEYS.MESSAGE_INFO]: message_info
        }
        window.parent.postMessage(message, targetOrigin);
      } catch (e) {
        // Silently fail
      }
    }
  }

  // Send functions
  const sendGrantedAccess = (accessToken: string, spreadsheetId: string) => {
    sendMessageToParent(CHILD_MESSAGE_TYPES.FRAME_TO_INTEGRATOR_AUTH_STATUS, { 
      [MESSAGE_KEYS.SPREADSHEET_ID]: spreadsheetId,
      [MESSAGE_KEYS.VALUE]: { accessToken, spreadsheetId }
    });
  };

  const sendAuthStatus = (spreadsheetId: string, isAuthorized: boolean) => {
    sendMessageToParent(CHILD_MESSAGE_TYPES.FRAME_TO_INTEGRATOR_AUTH_STATUS, {
      [MESSAGE_KEYS.SPREADSHEET_ID]: spreadsheetId,
      [MESSAGE_KEYS.VALUE]: isAuthorized
    });
  };

  const sendColumnOptions = useCallback((spreadsheetId: string, columns: string[]) => {
    sendMessageToParent(CHILD_MESSAGE_TYPES.FRAME_TO_INTEGRATOR_COLUMN_OPTIONS, {
      [MESSAGE_KEYS.SPREADSHEET_ID]: spreadsheetId,
      [MESSAGE_KEYS.VALUE]: columns
    });
  }, []);

  const sendTaskData = (spreadsheetId: string, data: any) => {
    sendMessageToParent(CHILD_MESSAGE_TYPES.FRAME_TO_INTEGRATOR_TASK_DATA, {
      [MESSAGE_KEYS.SPREADSHEET_ID]: spreadsheetId,
      [MESSAGE_KEYS.VALUE]: data
    });
  };

  const sendPaginatedTaskData = (spreadsheetId: string, data: any) => {
    sendMessageToParent(CHILD_MESSAGE_TYPES.FRAME_TO_INTEGRATOR_PAGINATED_TASK_DATA, {
      [MESSAGE_KEYS.SPREADSHEET_ID]: spreadsheetId,
      [MESSAGE_KEYS.VALUE]: data
    });
  };

  return { 
    sendGrantedAccess, 
    sendAuthStatus,
    sendColumnOptions,
    sendTaskData,
    sendPaginatedTaskData,
    sendMessageToParent,
  };
} 